import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatMoney } from "@/components/KPICard";
import {
  AlertCircle, CalendarClock, ShieldCheck, FileSignature, BellRing, ArrowRight,
} from "lucide-react";

const ICON_MAP = {
  payment: CalendarClock,
  contract: FileSignature,
  warranty: ShieldCheck,
  alert: BellRing,
};

const SEV_STYLE = {
  critical: "text-rose-500 border-rose-500/20 bg-rose-500/5",
  warning: "text-amber-500 border-amber-500/20 bg-amber-500/5",
  info: "text-sky-500 border-sky-500/20 bg-sky-500/5",
};

export default function UpcomingEventsWidget({ days = 15 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/dashboard/upcoming?days=${days}`).then((r) => {
      setEvents(r.data.events);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [days]);

  if (loading) return null;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 sm:p-6" data-testid="upcoming-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Próximos vencimientos</div>
          <div className="text-sm font-heading font-semibold mt-1">
            {events.length ? `${events.length} eventos en ${days} días` : "Nada urgente"}
          </div>
        </div>
        <AlertCircle className={`w-5 h-5 ${events.some((e) => e.severity === "critical") ? "text-rose-500" : "text-muted-foreground"}`} />
      </div>
      {events.length ? (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {events.map((e, i) => {
            const Icon = ICON_MAP[e.type] || BellRing;
            const tone = SEV_STYLE[e.severity] || SEV_STYLE.info;
            const to = e.property_id ? `/properties/${e.property_id}` : "/alerts";
            return (
              <Link
                key={i}
                to={to}
                className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${tone} hover:border-foreground/40`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate text-foreground">{e.title}</div>
                  {e.subtitle && <div className="text-[11px] text-muted-foreground truncate">{e.subtitle}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] num">
                    {e.days_out < 0 ? `Vencido ${Math.abs(e.days_out)}d` : e.days_out === 0 ? "Hoy" : `en ${e.days_out}d`}
                  </div>
                  {e.amount > 0 && (
                    <div className="text-[10px] text-muted-foreground num">{formatMoney(e.amount, e.currency)}</div>
                  )}
                </div>
                <ArrowRight className="w-3.5 h-3.5 opacity-40" />
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-sm text-muted-foreground">
          Sin vencimientos en los próximos {days} días. Todo en orden.
        </div>
      )}
    </div>
  );
}
